import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { Link2, BarChart3, MessageCircle, TrendingUp, FileText, CheckCircle2, Activity } from "lucide-react";
import { motion } from "framer-motion";
import PhoneMockup from "../components/PhoneMockup";

const features = [
  {
    icon: Link2,
    title: "LIS Integrations for Labs",
    description: "Enterprise-grade connectivity supporting HL7 | FHIR R4 | Secure automation",
  },
  {
    icon: BarChart3,
    title: "Analytics Dashboard",
    description: "Visual intelligence for lab operations, engagement & business performance.",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp Chatbot",
    description: "Instant results, smart summaries & doctor-friendly views.",
  },
];

const SectionContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  backgroundColor: "#ffffff",
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const FeatureTile = styled(motion.div)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  borderRadius: "16px",
  backgroundColor: "rgba(245, 242, 240, 0.5)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#FFFFFF",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    transform: "translateX(8px)",
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  backgroundColor: "rgba(53, 103, 214, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const FeatureContent = styled(Box)({
  flex: 1,
});

const FeatureTitle = styled(Typography)({
  fontWeight: 600,
  color: "#1D47A4",
  marginBottom: "0.5rem",
  fontSize: "1.125rem",
});

const FeatureDescription = styled(Typography)({
  fontSize: "0.875rem",
  color: "#4C4C4C",
  lineHeight: 1.6,
});

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
`;


// Mobile Dashboard Content Styles
const MobileDashboardWrapper = styled(Box)(({ theme }) => ({
  height: "100%",
  minHeight: "100%",
  background: "linear-gradient(180deg, #F8F9FA 0%, #FFFFFF 100%)",
  padding: theme.spacing(2),
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.25),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    gap: theme.spacing(1),
  },
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(0.5),
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(0.5),
  },
}));

const MobileTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 700,
  color: "#1D47A4",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.8125rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

const MobileLiveBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.4),
  padding: theme.spacing(0.3, 0.7),
  backgroundColor: "rgba(54, 152, 104, 0.15)",
  borderRadius: "10px",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.25, 0.6),
    gap: theme.spacing(0.3),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.2, 0.5),
    gap: theme.spacing(0.25),
  },
}));

const MobileLiveDot = styled(Box)(({ theme }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: "#369868",
  animation: `${pulse} 2s ease-in-out infinite`,
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "5px",
    height: "5px",
  },
}));

const MobileLiveText = styled(Typography)(({ theme }) => ({
  fontSize: "0.625rem",
  fontWeight: 600,
  color: "#369868",
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.5625rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.5rem",
  },
}));

const MobileMetricCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "12px",
  padding: theme.spacing(1.5),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1.25),
    borderRadius: "10px",
    gap: theme.spacing(0.65),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    borderRadius: "8px",
    gap: theme.spacing(0.5),
  },
}));

const MobileMetricRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const MobileMetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.625rem",
  color: "#4C4C4C",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.3,
  flex: 1,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.5625rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.5rem",
  },
}));

const MobileMetricValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 700,
  color: "#1D47A4",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
  marginTop: theme.spacing(0.25),
  [theme.breakpoints.down("md")]: {
    fontSize: "1.125rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const MobileMetricChange = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.4),
  padding: theme.spacing(0.4, 0.7),
  backgroundColor: "rgba(54, 152, 104, 0.1)",
  borderRadius: "6px",
  marginTop: theme.spacing(0.25),
  width: "fit-content",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.35, 0.6),
    gap: theme.spacing(0.35),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.3, 0.5),
    gap: theme.spacing(0.3),
  },
}));

const MobileChangeText = styled(Typography)(({ theme }) => ({
  fontSize: "0.5625rem",
  fontWeight: 600,
  color: "#369868",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.4375rem",
  },
}));

const MobileChartContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "12px",
  padding: theme.spacing(1.5),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  height: "120px",
  minHeight: "120px",
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(0.75),
  width: "100%",
  [theme.breakpoints.down("md")]: {
    height: "100px",
    minHeight: "100px",
    padding: theme.spacing(1.25),
    gap: theme.spacing(0.6),
  },
  [theme.breakpoints.down("sm")]: {
    height: "85px",
    minHeight: "85px",
    padding: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
}));

const MobileChartBar = styled(motion.div)(({ theme, height }) => ({
  flex: 1,
  height: `${height}%`,
  backgroundColor: "#3567D6",
  borderRadius: "4px 4px 0 0",
  minHeight: "4px",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "3px 3px 0 0",
  },
}));

// Mobile Dashboard Content Component
const MobileDashboardContent = () => {
  return (
    <MobileDashboardWrapper>
      <MobileHeader>
        <MobileTitle>Lab Dashboard</MobileTitle>
        <MobileLiveBadge>
          <MobileLiveDot />
          <MobileLiveText>Live</MobileLiveText>
        </MobileLiveBadge>
      </MobileHeader>

      <MobileMetricCard
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <MobileMetricRow>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.4, sm: 0.5 }, flex: 1, minWidth: 0 }}>
            <Box
              component={FileText}
              sx={{
                width: { xs: "12px", sm: "14px" },
                height: { xs: "12px", sm: "14px" },
                color: "#3567D6",
                flexShrink: 0
              }}
            />
            <MobileMetricLabel sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>Smart Reports</MobileMetricLabel>
          </Box>
        </MobileMetricRow>
        <MobileMetricValue>Active</MobileMetricValue>
        <MobileMetricChange>
          <Box
            component={CheckCircle2}
            sx={{
              width: { xs: "8px", sm: "10px" },
              height: { xs: "8px", sm: "10px" },
              color: "#369868",
              flexShrink: 0
            }}
          />
          <MobileChangeText>All systems operational</MobileChangeText>
        </MobileMetricChange>
      </MobileMetricCard>

      <MobileMetricCard
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <MobileMetricRow>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.4, sm: 0.5 }, flex: 1, minWidth: 0 }}>
            <Box
              component={BarChart3}
              sx={{
                width: { xs: "12px", sm: "14px" },
                height: { xs: "12px", sm: "14px" },
                color: "#4674D9",
                flexShrink: 0
              }}
            />
            <MobileMetricLabel sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>Report Generation</MobileMetricLabel>
          </Box>
        </MobileMetricRow>
        <MobileMetricValue>Real-time</MobileMetricValue>
        <MobileMetricChange>
          <Box
            component={Activity}
            sx={{
              width: { xs: "8px", sm: "10px" },
              height: { xs: "8px", sm: "10px" },
              color: "#369868",
              flexShrink: 0
            }}
          />
          <MobileChangeText>Automated workflow</MobileChangeText>
        </MobileMetricChange>
      </MobileMetricCard>

      <MobileMetricCard
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <MobileMetricRow>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.4, sm: 0.5 }, flex: 1, minWidth: 0 }}>
            <Box
              component={MessageCircle}
              sx={{
                width: { xs: "12px", sm: "14px" },
                height: { xs: "12px", sm: "14px" },
                color: "#1D47A4",
                flexShrink: 0
              }}
            />
            <MobileMetricLabel sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>Patient Engagement</MobileMetricLabel>
          </Box>
        </MobileMetricRow>
        <MobileMetricValue>Enhanced</MobileMetricValue>
        <MobileMetricChange>
          <Box
            component={TrendingUp}
            sx={{
              width: { xs: "8px", sm: "10px" },
              height: { xs: "8px", sm: "10px" },
              color: "#369868",
              flexShrink: 0
            }}
          />
          <MobileChangeText>Video reports enabled</MobileChangeText>
        </MobileMetricChange>
      </MobileMetricCard>

      <MobileChartContainer>
        {[65, 75, 82, 88, 85, 92, 90].map((height, index) => (
          <MobileChartBar
            key={index}
            height={height}
            initial={{ height: 0 }}
            whileInView={{ height: `${height}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
          />
        ))}
      </MobileChartContainer>
    </MobileDashboardWrapper>
  );
};

const FeatureTilesSection = () => {
  return (
    <SectionContainer>
      <Container>
        <Grid container spacing={6} alignItems="center">
          {/* Left - Feature Tiles */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {features.map((feature, index) => (
                <FeatureTile
                  key={feature.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <IconContainer>
                    <feature.icon style={{ width: "24px", height: "24px", color: "#3567D6" }} />
                  </IconContainer>
                  <FeatureContent>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>{feature.description}</FeatureDescription>
                  </FeatureContent>
                </FeatureTile>
              ))}
            </Box>
          </Grid>

          {/* Right - Mobile Analytics Dashboard Mockup */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
              >
                <PhoneMockup>
                  <MobileDashboardContent />
                </PhoneMockup>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </SectionContainer>
  );
};

export default FeatureTilesSection;